import { faBox } from '@fortawesome/free-solid-svg-icons';
import { faEdit, faExclamationTriangle, faInfoCircle, faLink, faWrench } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ConvertedUnits from 'src/components/ConvertedUnits';
import ActionPanelWrapper from 'src/components/ui/ActionPanelWrapper';
import Alert from 'src/components/ui/Alert';
import ResourceCard from 'src/components/ui/ResourceCard';
import ResourceHeader from 'src/components/ui/ResourceHeader';
import { INVENTORY_ACTION_TYPES } from 'src/constants/inventory';
import {
  useStockActionRelationshipsForPiece,
  useStockActionsForRun } from 'src/hooks/services/useStockActions';
import InventorySelectContainers from 'src/pages/run/[uuid]/panels/InventorySelectContainers';
import useActionPanelStore from 'src/stores/useActionPanelStore';
import { apiWithContext, fetchMaterialsAndLocations, fetchStocksWithMaterialLots } from 'src/utils/api';
import { MATERIAL_TYPES, MATERIAL_TYPES_MAP } from 'src/utils/constants';
import {
  findPiecesWithIndividualAssignments,
  getStockUrisFromRelationships,
  isGroupAssignment,
  isIndividualAssignment,
} from 'src/utils/inventoryManagementUtils';
import routes from 'src/utils/routes';
import { pluralWord } from 'src/utils/stringUtils';
import { getRouteURI, getUuid } from 'src/utils/url';

/**
 * Panel component that displays stock materials assigned to pieces or instruction groups
 *
 * Features:
 * - Shows group-level material assignments for instruction groups
 * - Shows individual + inherited material assignments for single pieces
 * - Tracks pieces with individual assignments for warnings
 * - Provides edit functionality for material quantities
 */
const StockMaterialsPanel = ({
  pieces,
  runUri,
  isInstructionGroup,
  groupIndex,
  pieceName,
  pieceGroupIndex,
}) => {
  const [stocks, setStocks] = useState([]);
  const [materials, setMaterials] = useState({});
  const [locations, setLocations] = useState({});
  const [loading, setLoading] = useState(true);
  const [stockError, setStockError] = useState(null);
  const [piecesWithIndividualAssignments, setPiecesWithIndividualAssignments] = useState([]);

  // Track the data signature to detect real changes and prevent unnecessary refetches
  const dataSignatureRef = useRef('');

  const { openActionPanel, closeActionPanel } = useActionPanelStore();

  // Fetch all stock actions for this run
  const {
    data: stockActions = [],
    isInitialLoading: stockActionsLoading,
    error: stockActionsError,
  } = useStockActionsForRun(runUri);

  // For individual pieces, fetch relationships to get group inheritance
  const pieceUri = !isInstructionGroup && pieces.length === 1 ? pieces[0] : null;
  const {
    data: pieceRelationships = [],
    isInitialLoading: relationshipsLoading,
    error: relationshipsError,
  } = useStockActionRelationshipsForPiece(pieceUri, runUri);

  /**
   * Collects stock URIs for instruction group view
   * - Finds group assignments (actions with multiple pieces matching this group)
   * - Tracks individual piece assignments within the group for warnings
   */
  const collectGroupStockUris = useCallback((stockActions, pieces) => {
    const uniqueStockUris = new Set();

    stockActions.forEach(action => {
      // Find group assignments matching this instruction group
      if (isGroupAssignment(action, pieces)) {
        uniqueStockUris.add(action.source_stock);
      }
    });

    // Find pieces with individual assignments
    const piecesWithIndividual = findPiecesWithIndividualAssignments(stockActions, pieces);
    setPiecesWithIndividualAssignments(piecesWithIndividual);

    // Transform set into array
    return Array.from(uniqueStockUris);
  }, []);

  /**
   * Collects stock URIs for individual piece view
   * - Gets stocks from group relationships (inherited from instruction groups)
   * - Gets stocks from direct individual assignments
   */
  const collectIndividualPieceStockUris = useCallback((stockActions, pieceRelationships, pieceUri) => {
    const uniqueStockUris = new Set();

    // Add stocks from group relationships (inherited materials)
    const relationshipStockUris = getStockUrisFromRelationships(pieceRelationships, stockActions);
    relationshipStockUris.forEach(uri => uniqueStockUris.add(uri));

    // Add stocks from direct individual assignments
    stockActions.forEach(action => {
      if (isIndividualAssignment(action, pieceUri)) {
        uniqueStockUris.add(action.source_stock);
      }
    });

    return Array.from(uniqueStockUris);
  }, []);

  useEffect(() => {
    // Wait for required data to be available
    if (stockActionsLoading || !stockActions) {
      return;
    }

    // For individual pieces, also wait for relationships to load
    if (!isInstructionGroup && pieceUri && relationshipsLoading) {
      return;
    }

    // Create a signature of the current data to detect real changes
    const stockActionUris = stockActions.map(action => action.uri).sort().join(',');
    const relationshipUris = pieceRelationships.map(rel => rel.uri).sort().join(',');
    const currentSignature = `${isInstructionGroup ? 'group' : 'piece'}|${stockActionUris}|${relationshipUris}|${pieces.join(',')}`;

    // Skip if data hasn't actually changed
    if (dataSignatureRef.current === currentSignature) {
      return;
    }

    const fetchStockDetails = async () => {
      dataSignatureRef.current = currentSignature;
      setLoading(true);

      try {
        const api = apiWithContext({});

        // Collect stock URIs based on view type (group vs individual)
        let stockUris;
        if (isInstructionGroup) {
          stockUris = collectGroupStockUris(stockActions, pieces);
        } else {
          stockUris = collectIndividualPieceStockUris(stockActions, pieceRelationships, pieceUri);
        }

        // Handle empty stock list
        if (stockUris.length === 0) {
          setStocks([]);
          setMaterials({});
          setLocations({});
          setLoading(false);
          return;
        }

        // Fetch detailed stock information with material lots
        const stocksWithLots = await fetchStocksWithMaterialLots(stockUris, api);
        setStocks(stocksWithLots);

        // Fetch related materials and locations
        const { materials: materialsData, locations: locationsData } =
          await fetchMaterialsAndLocations(stocksWithLots, api);

        setMaterials(materialsData);
        setLocations(locationsData);
      } catch (error) {
        console.error('Error fetching stock details:', error);
        setStockError(error);
      } finally {
        setLoading(false);
      }
    };

    void fetchStockDetails();
  }, [
    stockActionsLoading,
    stockActions,
    pieceRelationships,
    isInstructionGroup,
    pieces,
    pieceUri,
    relationshipsLoading,
    collectGroupStockUris,
    collectIndividualPieceStockUris,
  ]);

  // Aggregate loading and error states
  const isLoadingData = loading || stockActionsLoading || relationshipsLoading;
  const error = stockError || stockActionsError || relationshipsError;

  /**
   * Calculates quantities for instruction group view
   * Returns the total group assignment quantity for each stock
   */
  const calculateGroupQuantities = useCallback((stocks, stockActions, pieces, materials) => {
    const quantities = {};

    stocks.forEach(stock => {
      const isBaseOrSupport = [MATERIAL_TYPES.BASE, MATERIAL_TYPES.SUPPORT]
        .includes(materials[stock.material]?.type);

      const matchingActions = stockActions.filter(action =>
        action.source_stock === stock.uri && isGroupAssignment(action, pieces)
      );

      // Base/support creates multiple delta actions that must be summed;
      // raw/consumable backend guarantees a single summed action.
      const qty = isBaseOrSupport
        ? matchingActions.reduce((sum, action) => sum + (action.action_quantity || 0), 0)
        : (matchingActions[0]?.action_quantity || 0);

      const absGroupQuantity = Math.abs(qty);
      quantities[stock.uri] = {
        quantity: absGroupQuantity,
        total: absGroupQuantity, // Same as quantity for groups
        units: materials[stock.material]?.units || 'N/A',
      };
    });

    return quantities;
  }, []);

  /**
   * Calculates quantities for individual piece view
   * Returns both individual assignments and inherited group shares
   */
  const calculateIndividualQuantities = useCallback((stocks, stockActions, pieceRelationships, pieceUri, materials) => {
    const quantities = {};

    stocks.forEach(stock => {
      const isBaseOrSupport = [MATERIAL_TYPES.BASE, MATERIAL_TYPES.SUPPORT]
        .includes(materials[stock.material]?.type);

      const individualActions = stockActions.filter(action =>
        action.source_stock === stock.uri && isIndividualAssignment(action, pieceUri)
      );

      // Base/support creates multiple delta actions that must be summed;
      // raw/consumable backend guarantees a single summed action.
      const individualQuantity = isBaseOrSupport
        ? individualActions.reduce((sum, action) => sum + (action.action_quantity || 0), 0)
        : (individualActions[0]?.action_quantity || 0);

      // Calculate inherited quantity from group relationships
      const groupRelationships = pieceRelationships.filter(relationship => {
        const stockAction = stockActions.find(currentAction => currentAction.uri === relationship.stock_action);
        return stockAction &&
               stockAction.source_stock === stock.uri &&
               !isIndividualAssignment(stockAction); // Only group assignments
      });

      const groupQuantity = groupRelationships
        .reduce((sum, relationship) => sum + (relationship.quantity || 0), 0);

      quantities[stock.uri] = {
        quantity: Math.abs(individualQuantity),
        total: Math.abs(individualQuantity + groupQuantity),
        units: materials[stock.material]?.units || 'N/A',
      };
    });

    return quantities;
  }, []);

  /**
   * Main quantity calculation that delegates to appropriate method based on view type
   */
  const stockQuantities = useMemo(() => {
    if (isInstructionGroup) {
      return calculateGroupQuantities(stocks, stockActions, pieces, materials);
    } else {
      return calculateIndividualQuantities(stocks, stockActions, pieceRelationships, pieceUri, materials);
    }
  }, [
    stocks,
    stockActions,
    isInstructionGroup,
    pieces,
    pieceUri,
    pieceRelationships,
    materials,
    calculateGroupQuantities,
    calculateIndividualQuantities,
  ]);

  /**
   * Opens the inventory selection panel for editing stock material quantities
   * Determines the appropriate action type (GROUP vs PIECE) and sets up initial values
   */
  const handleEdit = useCallback((stock) => {
    const action = isInstructionGroup ? INVENTORY_ACTION_TYPES.GROUP : INVENTORY_ACTION_TYPES.PIECE;

    // Use the first available material lot as the initial selection
    const initialLotUri = stock.material_lots?.length > 0 ? stock.material_lots[0] : null;

    openActionPanel({
      panelId: 'edit-stock-materials',
      title: 'Edit Quantity',
      stack: true,
      content: (
        <InventorySelectContainers
          stock={stock}
          initialLotUri={initialLotUri}
          scannedContainerUri={null}
          action={action}
          runUri={runUri}
          pieces={pieces}
          pieceName={pieceName}
          pieceGroupIndex={pieceGroupIndex}
        />
      ),
    });
  }, [isInstructionGroup, openActionPanel, runUri, pieces, pieceName, pieceGroupIndex]);

  /**
   * Renders a single stock card with material, location, and quantity information
   */
  const renderStockCard = useCallback((stock) => {
    const material = materials[stock.material];
    const location = locations[stock.location];
    const qty = stockQuantities[stock.uri];
    const isBaseOrSupportMaterial = [MATERIAL_TYPES.BASE, MATERIAL_TYPES.SUPPORT]
      .includes(material?.type)
    const materialType = MATERIAL_TYPES_MAP[material?.type];
    const materialLabel = materialType && `${materialType} Material`;

    // Show edit button for groups OR individual pieces with individual quantities
    const hasIndividualQuantity = !isInstructionGroup && qty?.quantity > 0;
    const showActionButton = (isInstructionGroup || hasIndividualQuantity) && !isBaseOrSupportMaterial;

    // Check if this is a group-only assignment (no individual quantity)
    const isGroupOnlyAssignment = isInstructionGroup || (qty?.quantity === 0 && qty?.total > 0);

    return (
      <ResourceCard
        key={stock.uri}
        icon={faBox}
        title={material?.name || '[Material Not Rendered]'}
        subtitle={location?.name || '[Location Not Rendered]'}
        label={isGroupOnlyAssignment ? 'Group' : null}
        customLabel={isBaseOrSupportMaterial && materialLabel}
        actionIcon={faEdit}
        showActionButton={showActionButton}
        actionButtonTitle="Edit stock assignment"
        onAction={() => handleEdit(stock)}
      >
        <div className="resource-card__quantities">
          <div className="resource-card__quantity-item">
            <span className="resource-card__quantity-label">Quantity</span>
            <div className="resource-card__quantity-value">
              <ConvertedUnits
                displayZero
                quantity={qty?.quantity || 0}
                units={qty?.units || 'N/A'}
                skipTooltip={false}
              />
            </div>
          </div>

          {!isInstructionGroup && (
            <div className="resource-card__quantity-item">
              <span className="resource-card__quantity-label">Total</span>
              <div className="resource-card__quantity-value">
                <ConvertedUnits
                  displayZero
                  quantity={qty?.total || 0}
                  units={qty?.units || 'N/A'}
                  skipTooltip={false}
                />
              </div>
            </div>
          )}
        </div>
      </ResourceCard>
    );
  }, [materials, locations, stockQuantities, isInstructionGroup, handleEdit]);

  /**
   * Separates stocks into individual and group-only for individual piece view
   */
  const { individualStocks, groupOnlyStocks } = useMemo(() => {
    if (isInstructionGroup) {
      return { individualStocks: [], groupOnlyStocks: [] };
    }

    const individual = stocks.filter(stock => {
      const qty = stockQuantities[stock.uri];
      return qty?.quantity > 0;
    });

    const groupOnly = stocks.filter(stock => {
      const qty = stockQuantities[stock.uri];
      return qty?.quantity === 0 && qty?.total > 0;
    });

    return { individualStocks: individual, groupOnlyStocks: groupOnly };
  }, [stocks, stockQuantities, isInstructionGroup]);

  /**
   * Renders the stock list content based on the current view type
   * Returns empty state, instruction group list, or individual piece list with separator
   */
  const renderStockListContent = useCallback(() => {
    // Empty state when no stock materials are assigned
    if (!stocks.length) {
      return (
        <div className="stock-materials-panel__empty">
          No stock materials assigned
        </div>
      );
    }

    // For instruction groups, render all stocks normally
    if (isInstructionGroup) {
      return (
        <div className="stock-materials-panel__list">
          {stocks.map(renderStockCard)}
        </div>
      );
    }

    // For individual pieces, show individual assignments first, then group-only stocks
    return (
      <div className="stock-materials-panel__list">
        {/* Render individual assignment stocks first */}
        {individualStocks.map(renderStockCard)}

        {/* Show separator if we have both types */}
        {individualStocks.length > 0 && groupOnlyStocks.length > 0 && (
          <div className="stock-materials-panel__separator">
            <div className="stock-materials-panel__separator-line" />
            <div className="stock-materials-panel__separator-text">
              Inherited from instruction group
            </div>
            <div className="stock-materials-panel__separator-line" />
          </div>
        )}

        {/* Render group-only stocks after separator */}
        {groupOnlyStocks.map(renderStockCard)}
      </div>
    );
  }, [stocks, isInstructionGroup, individualStocks, groupOnlyStocks, renderStockCard]);

  return (
    <ActionPanelWrapper isLoading={isLoadingData} error={error} onClose={closeActionPanel}>
      <ResourceHeader
        icon={faWrench}
        action={
          !isInstructionGroup && pieceName ? (
            <Link
              to={getRouteURI(routes.pieceOverview, { uuid: getUuid(pieceUri) })}
              className="panel-header__action-icon"
              title="View piece details"
            >
              <FontAwesomeIcon icon={faLink} />
            </Link>
          ) : null
        }
      >
        {isInstructionGroup ? (
          // Instruction Group Header: Shows group index and piece count
          <>
            <div className="panel-header__title">
              Instruction Group {groupIndex}
            </div>
            <div className="panel-header__subtitle">
              {pieces.length} {pluralWord('piece', pieces)}
            </div>
          </>
        ) : (
          // Individual Piece Header: Shows piece name with navigation link
          pieceName && (
            <>
              <div className="panel-header__title">
                <Link
                  to={getRouteURI(routes.pieceOverview, { uuid: getUuid(pieceUri) })}
                  className="panel-header__link"
                >
                  {pieceName}
                </Link>
              </div>
              <div className="panel-header__subtitle">
                Individual piece
              </div>
            </>
          )
        )}
      </ResourceHeader>

      {/* Shows warning when instruction group has pieces with individual assignments */}
      {isInstructionGroup && piecesWithIndividualAssignments.length > 0 && (
        <Alert
          variant="warning"
          icon={faExclamationTriangle}
          title="Individual material assignments"
        >
          Please note, <strong>{piecesWithIndividualAssignments.length} {pluralWord('piece', piecesWithIndividualAssignments)}</strong> {piecesWithIndividualAssignments.length !== 1 ? 'have' : 'has'} individual material assignments.
          You can expand each individual piece to see other material individual assignments.
        </Alert>
      )}

      {/* Info banner explaining "Total" column for individual pieces */}
      {!isInstructionGroup && stocks.length > 0 && (
        <Alert
          variant="info"
          icon={faInfoCircle}
          title='Understanding "Total" quantities'
        >
          <strong>Total</strong> combines material from the instruction group plus any individual stock assigned specifically to this piece.
        </Alert>
      )}

      {renderStockListContent()}
    </ActionPanelWrapper>
  );
};

StockMaterialsPanel.propTypes = {
  pieces: PropTypes.arrayOf(PropTypes.string).isRequired,
  runUri: PropTypes.string.isRequired,
  isInstructionGroup: PropTypes.bool,
  groupIndex: PropTypes.number,
  pieceName: PropTypes.string,
  pieceGroupIndex: PropTypes.number,
};

StockMaterialsPanel.defaultProps = {
  isInstructionGroup: false,
  groupIndex: 1,
  pieceName: null,
  pieceGroupIndex: null,
};

export default StockMaterialsPanel;

